import { ExpandAltOutlined } from '@ant-design/icons';
import { Button, Space } from 'antd';
import { ColumnType } from 'antd/es/table/interface';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { RingComplianceReport } from 'business/quality-control/pages/ring-compliance-report/components/ring-compliance-table';
import { formatDate } from 'technical/format/date';

const DateCell = ({ date }: { date: string | null }) => {
  if (!date) {
    return <span>-</span>;
  }

  return (
    <Space size="small">
      <span>{formatDate(date)}</span>
    </Space>
  );
};

export const useRingComplianceReportColumns = (
  onExpand: (id: string) => void,
  isMobile: boolean,
) => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No construction site');

  const { t } = useTranslation();

  return useMemo(() => {
    const commonColumnProps: Partial<ColumnType<RingComplianceReport>> = {
      align: 'center',
    };

    const actionColumnProps: Partial<ColumnType<RingComplianceReport>> = {
      align: 'end',
    };

    return [
      {
        title: t('qualityControl.ringComplianceReport.ring'),
        dataIndex: 'ringNumber',
        key: 'ringNumber',
        sorter: true,
        width: 100,
        ...commonColumnProps,
      },
      {
        title: t('qualityControl.ringComplianceReport.complianceDate'),
        dataIndex: 'complianceDate',
        key: 'complianceDate',
        sorter: true,
        render: (date: string) => {
          return <DateCell date={date} />;
        },
        width: 200,
        ...commonColumnProps,
      },
      ...(currentConstructionSite.isQualityValidatorActivated
        ? [
            {
              title: t(
                'qualityControl.ringComplianceReport.approvalOrRefusalDate',
              ),
              key: 'approvalRefusalDate',
              sorter: true,
              render: (_: string, record: RingComplianceReport) => {
                return (
                  <DateCell date={record.approvalDate ?? record.refusalDate} />
                );
              },
              width: 200,
              ...commonColumnProps,
            },
          ]
        : []),
      {
        title: t('qualityControl.ringComplianceReport.status'),
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        render: (status: string) => {
          return t(
            'qualityControl.ringComplianceReport.statusSearchable.state',
            { context: status },
          );
        },
        width: 200,
        ...commonColumnProps,
      },
      ...(!isMobile
        ? [
            {
              title: t('qualityControl.ringComplianceReport.actions.header'),
              key: 'actions',
              render: (_: unknown, record: RingComplianceReport) => {
                return (
                  <Button
                    type="text"
                    icon={<ExpandAltOutlined />}
                    onClick={() => onExpand(record.id)}
                  />
                );
              },
              width: 75,
              ...actionColumnProps,
            },
          ]
        : []),
    ] satisfies ColumnType<RingComplianceReport>[];
  }, [
    t,
    onExpand,
    currentConstructionSite.isQualityValidatorActivated,
    isMobile,
  ]);
};
